import { useCallback, useEffect, useRef, useState } from "react";
import { checkTenantHealth, isSaasMode } from "@/lib/api";

const POLL_MS = 30_000;

// Auto-recheck for the tenant status screens (/tenant-not-found, /expired). Every 30s (and on demand)
// it re-probes the backend health. When the tenant comes online it reloads into the app; if the state
// flips (not-found <-> expired) it routes to the matching screen. SaaS-only; a no-op otherwise.
//   screen: "notfound" | "expired"
//   returns { status: "idle"|"checking"|"reconnected", recheckNow }
export function useTenantRecheck(screen) {
  const [status, setStatus] = useState("idle");
  const busy = useRef(false);
  const done = useRef(false); // stop once we start navigating away

  const runCheck = useCallback(async () => {
    if (busy.current || done.current || !isSaasMode()) return;
    busy.current = true;
    setStatus("checking");
    try {
      const verdict = await checkTenantHealth();
      if (done.current) return;
      if (verdict === "healthy") {
        done.current = true;
        setStatus("reconnected");
        setTimeout(() => window.location.replace("/"), 800);
        return;
      }
      if (screen === "notfound" && verdict === "expired") {
        done.current = true;
        window.location.replace("/expired");
        return;
      }
      if (screen === "expired" && verdict === "gone") {
        done.current = true;
        window.location.replace("/tenant-not-found");
        return;
      }
      setStatus("idle"); // still the same state — keep waiting
    } finally {
      busy.current = false;
    }
  }, [screen]);

  useEffect(() => {
    if (!isSaasMode()) return undefined;
    const id = setInterval(runCheck, POLL_MS);
    return () => clearInterval(id);
  }, [runCheck]);

  return { status, recheckNow: runCheck };
}
